// Ana sayfa — Blazor HomePage: 12 kolonlu widget ızgarası (react-grid-layout), düzenleme modunda
// sürükle/boyutlandır + widget ekle/kaldır, yerleşim `UserSettings["WidgetLayout"]` içinde Blazor ile ortak.
// Mobilde tek sütun (sürükleme kapalı). Tercihlerde "sade" ana sayfa seçiliyse basit favori listesi gösterilir.
import { useMemo, useState } from 'react';
import GridLayout, { type Layout } from 'react-grid-layout';
import { LayoutGrid, Plus, RotateCcw, Star } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '@/entities/session/model/authStore';
import { usePrefsStore } from '@/entities/preferences/model/prefsStore';
import { NavIcon } from '@/features/shell/navMenu/NavIcon';
import { useMenu } from '@/features/shell/navMenu/useMenu';
import { useOpenScreen } from '@/features/shell/tabs/useOpenScreen';
import { useBreakpoint } from '@/shared/hooks/useBreakpoint';
import { Button } from '@/shared/ui/Button';
import { ScreenLoader } from '@/shared/ui/ScreenLoader';
import { OctoMark } from '@/shared/ui/OctoMark';
import { useElementWidth } from './hooks/useElementWidth';
import { useWidgetLayout } from './hooks/useWidgetLayout';
import { GRID_COLUMNS, ROW_HEIGHT, addWidget, applyGridLayout, removeWidget, stackedOrder, widgetConstraints, type WidgetType } from './model/widgetLayout';
import { WidgetFrame } from './ui/WidgetFrame';
import { WidgetPicker } from './ui/WidgetPicker';
import { WidgetRenderer } from './ui/WidgetRenderer';
import { useWidgetTitle } from './ui/useWidgetTitle';
import type { WidgetDefinition } from './model/widgetLayout';
import 'react-grid-layout/css/styles.css';

function WidgetCell({ widget, editing, onRemove }: { widget: WidgetDefinition; editing: boolean; onRemove: () => void }) {
  const title = useWidgetTitle(widget);
  return (
    <WidgetFrame title={title} editing={editing} onRemove={onRemove}>
      <WidgetRenderer widget={widget} />
    </WidgetFrame>
  );
}

/** Tercihlerde homeLayout='simple' seçiliyse gösterilen sade favori listesi. */
function SimpleHome() {
  const { t } = useTranslation();
  const user = useAuthStore((s) => s.user);
  const { favorites } = useMenu();
  const openScreen = useOpenScreen();
  const hour = new Date().getHours();
  const greeting = hour < 12 ? t('home.goodMorning') : hour < 18 ? t('home.goodDay') : t('home.goodEvening');
  return (
    <div className="mx-auto max-w-5xl space-y-6 p-4">
      {/* Karşılama kartında logomark: ana sayfa uygulamanın "kapağı" ve markanın
          görüneceği yer burası; başlıktaki 32 px'lik işaret bu ölçekte kaybolur. */}
      <section className="border-border bg-bg-elevated flex items-center gap-4 rounded-xl border p-5">
        <OctoMark className="h-12 w-12 shrink-0" />
        <div className="min-w-0">
          <h1 className="text-xl font-semibold">
            {greeting}, {user?.userName}
          </h1>
          <p className="text-fg-muted text-sm">{t('app.name')}</p>
        </div>
      </section>
      <section>
        <h2 className="text-fg-muted mb-2 flex items-center gap-1 text-sm font-semibold">
          <Star className="h-4 w-4" /> {t('shell.favorites')}
        </h2>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
          {favorites.map((f) => (
            <button key={f.screenId} type="button" onClick={() => openScreen(f)} className="border-border bg-bg-elevated hover:border-brand-500 hover:bg-bg-hover flex items-center gap-2 rounded-lg border p-3 text-left text-sm">
              <NavIcon node={f} className="h-5 w-5" />
              <span className="truncate">{f.caption}</span>
            </button>
          ))}
        </div>
      </section>
    </div>
  );
}

export function HomePage() {
  const { t } = useTranslation();
  const { isMobile } = useBreakpoint();
  const homeLayout = usePrefsStore((s) => s.prefs.homeLayout);
  const { widgets, isLoading, update, reset } = useWidgetLayout();
  const [editing, setEditing] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const { width, ref: measureRef } = useElementWidth();

  // Boyut sınırları widget tipine göre; hepsine aynı minW/minH vermek takvimi okunmaz
  // hale getirecek kadar küçültmeye izin veriyordu.
  const layout = useMemo<Layout>(
    () => (widgets ?? []).map((w) => ({ i: w.id, x: w.x, y: w.y, w: w.w, h: w.h, ...widgetConstraints(w.type) })),
    [widgets],
  );

  if (homeLayout === 'simple') return <SimpleHome />;
  if (isLoading || !widgets) {
    return (
      <ScreenLoader kind="screen" className="h-full" />
    );
  }

  const onRemove = (id: string) => update(removeWidget(widgets, id));
  const onAdd = (type: WidgetType) => update(addWidget(widgets, type));

  const toolbar = (
    <div className="flex flex-wrap items-center gap-2 px-4 pt-4">
      <span className="flex-1" />
      {editing ? (
        <>
          <Button size="sm" variant="secondary" onClick={() => setPickerOpen(true)}>
            <Plus className="h-4 w-4" /> {t('home.addWidget')}
          </Button>
          <Button size="sm" variant="ghost" onClick={reset}>
            <RotateCcw className="h-4 w-4" /> {t('home.resetLayout')}
          </Button>
        </>
      ) : null}
      <Button size="sm" variant={editing ? 'primary' : 'secondary'} onClick={() => setEditing((v) => !v)} data-testid="home-edit-toggle">
        <LayoutGrid className="h-4 w-4" /> {editing ? t('home.doneEditing') : t('home.editLayout')}
      </Button>
    </div>
  );

  /*
   * Mobil: tek sütun, sürükleme yok — bu yüzden DÜZEN ARAÇ ÇUBUĞU DA YOK.
   *
   * Kartlar burada sabit sırada akıyor; "Düzeni düzenle" açıldığında yapılabilecek
   * tek şey kart eklemek/çıkarmaktı ve düğme, dar ekranda içeriğin üstünde yer
   * kaplıyordu. Düzen masaüstünde kurulur, telefonda görüntülenir.
   */
  if (isMobile) {
    return (
      <div className="flex min-h-0 flex-1 flex-col">
        <div className="min-h-0 flex-1 space-y-3 overflow-auto p-4">
          {stackedOrder(widgets).map((w) => (
            <div key={w.id} style={{ height: Math.max(2, w.h) * ROW_HEIGHT }}>
              {/* Düzenleme yok: kart başlığındaki kaldır düğmesi de çıkmaz. */}
              <WidgetCell widget={w} editing={false} onRemove={() => onRemove(w.id)} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {toolbar}
      <div className="min-h-0 flex-1 overflow-auto p-4">
        {/* Ölçüm iç kapsayıcıda: dolgu hariç gerçek genişlik, aksi hâlde ızgara sağa taşar */}
        <div ref={measureRef}>
        {width > 0 ? (
          <GridLayout
            layout={layout}
            width={width}
            gridConfig={{ cols: GRID_COLUMNS, rowHeight: ROW_HEIGHT, margin: [12, 12] }}
            dragConfig={{ enabled: editing, handle: '.widget-drag-handle' }}
            resizeConfig={{ enabled: editing }}
            onLayoutChange={(next: Layout) => {
              if (editing) update(applyGridLayout(widgets, [...next]));
            }}
          >
            {widgets.map((w) => (
              <div key={w.id}>
                <WidgetCell widget={w} editing={editing} onRemove={() => onRemove(w.id)} />
              </div>
            ))}
          </GridLayout>
        ) : null}
        </div>
      </div>
      <WidgetPicker open={pickerOpen} onOpenChange={setPickerOpen} widgets={widgets} onAdd={onAdd} />
    </div>
  );
}
